import { api } from "../api";
import { supabase } from "../supabase";
import { Profile } from "./Profile";
import { Billing } from "./Billing";
import { Security } from "./Security";
import { NotificationPrefs } from "./NotificationPrefs";
import { TimezoneSetting } from "./TimezoneSetting";
import { Appearance } from "./Appearance";

/**
 * The Control Center: a single "my account" hub consolidating every account
 * surface into clear sections — Connected accounts, Billing & credits,
 * Notifications, Appearance, Security, and the Danger zone. Rendered as the
 * full-page Settings view in the workspace area (App's `nav === "settings"`).
 *
 * Returns a fragment so each section stays a direct child of the page's
 * `.cc-settings-sections` wrapper, inheriting its vertical rhythm (the flex gap)
 * rather than nesting.
 */
export function ControlCenter({
  email,
  name,
  onError,
  timezone,
  onSetTimezone,
}: {
  email?: string;
  /** Current display name (from the auth user's metadata), for the Profile field. */
  name: string;
  /** Surface an account-deletion failure on the app's banner. */
  onError: (text: string) => void;
  /** The effective IANA timezone (chosen, or browser default). */
  timezone: string;
  /** Persist a new timezone choice (optimistic; reverts on failure). */
  onSetTimezone: (tz: string) => void;
}) {
  async function deleteAccount() {
    if (
      !window.confirm("Permanently delete your account and all data? This cannot be undone.")
    ) {
      return;
    }
    try {
      await api.deleteAccount();
      await supabase.auth.signOut();
    } catch (err) {
      // Never leave the user believing their account was deleted when the
      // request failed — say so plainly.
      onError(err instanceof Error ? err.message : "Couldn't delete your account.");
    }
  }

  return (
    <>
      <Profile initialName={name} />
      <Billing />
      <TimezoneSetting timezone={timezone} onSetTimezone={onSetTimezone} />
      <NotificationPrefs />
      <Appearance />
      <Security email={email} />
      {/* Account deletion lives here (occasional, destructive) rather than in the
          footer where it was one stray click from disaster. */}
      <div className="card danger-zone">
        <h2>Danger zone</h2>
        <p className="muted small">
          Permanently delete your account, all connected platforms, scheduled posts, and history.
          This cannot be undone.
        </p>
        <button type="button" className="btn danger" onClick={() => void deleteAccount()}>
          Delete account
        </button>
      </div>
    </>
  );
}
